/**
 * Birth Data Form Component
 * Collects date, time, and location for natal chart calculation
 */

import { useState, useCallback, useEffect } from 'react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useTelegramApp, useLocationSearch } from '../../hooks';
import type { BirthData, LocationSearchResult } from '../../types';
import styles from './BirthDataForm.module.css';

interface BirthDataFormProps {
  onSubmit: (data: BirthData) => void;
  initialData?: Partial<BirthData>;
}

export function BirthDataForm({ onSubmit, initialData }: BirthDataFormProps) {
  const { haptic, showMainButton, showMainButtonDisabled, hideMainButton, enableClosingConfirmation, disableClosingConfirmation } = useTelegramApp();

  // Form state
  const [name, setName] = useState(initialData?.name || '');
  const [date, setDate] = useState(initialData?.date || '');
  const [time, setTime] = useState(initialData?.time || '');
  const [timeKnown, setTimeKnown] = useState(initialData?.timeKnown ?? true);

  // Location search
  const {
    query: locationQuery,
    setQuery: setLocationQuery,
    results: locationResults,
    isLoading: isSearchingLocation,
    error: locationError,
    hasSearched: locationSearched,
    selectedLocation,
    selectLocation,
    clearSelection,
  } = useLocationSearch();

  // Form validation
  const isValid = date && (time || !timeKnown) && selectedLocation;

  // Handle form submission
  const handleSubmit = useCallback(() => {
    if (!isValid || !selectedLocation) return;

    haptic.notification('success');

    const birthData: BirthData = {
      name: name || undefined,
      date,
      time: timeKnown ? time : '12:00',
      timeKnown,
      location: {
        displayName: selectedLocation.displayName,
        displayNameShort: selectedLocation.displayNameShort,
        latitude: selectedLocation.latitude,
        longitude: selectedLocation.longitude,
        countryCode: selectedLocation.countryCode,
        timezoneStr: selectedLocation.timezoneStr,
      },
    };

    onSubmit(birthData);
  }, [isValid, selectedLocation, name, date, time, timeKnown, haptic, onSubmit]);

  // Update main button — always visible, disabled until form is valid
  useEffect(() => {
    if (isValid) {
      showMainButton('Рассчитать карту', handleSubmit);
    } else {
      showMainButtonDisabled('Рассчитать карту');
    }

    return () => hideMainButton();
  }, [isValid, showMainButton, showMainButtonDisabled, hideMainButton, handleSubmit]);

  // Enable closing confirmation when form has data
  useEffect(() => {
    const hasData = !!(name || date || time || selectedLocation);
    if (hasData) {
      enableClosingConfirmation();
    } else {
      disableClosingConfirmation();
    }

    return () => disableClosingConfirmation();
  }, [name, date, time, selectedLocation, enableClosingConfirmation, disableClosingConfirmation]);

  // Handle location selection
  const handleLocationSelect = (location: LocationSearchResult) => {
    haptic.selection();
    selectLocation(location);
  };

  // Format date for display
  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      const dateObj = new Date(dateStr);
      return format(dateObj, 'd MMMM yyyy', { locale: ru });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Натальная карта</h1>
      <p className={styles.subtitle}>Введите данные рождения</p>

      <div className={styles.form}>
        {/* Name (optional) */}
        <div className={styles.field}>
          <label htmlFor="birth-name" className={styles.label}>Имя (необязательно)</label>
          <input
            id="birth-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Например: Мария"
            className={styles.input}
            maxLength={100}
          />
        </div>

        {/* Date */}
        <div className={styles.field}>
          <label htmlFor="birth-date" className={styles.label}>Дата рождения</label>
          <input
            id="birth-date"
            type="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              haptic.selection();
            }}
            className={styles.input}
            max={new Date().toISOString().split('T')[0]}
            min="1800-01-01"
          />
          {date && (
            <span className={styles.hint}>{formatDateDisplay(date)}</span>
          )}
        </div>

        {/* Time */}
        <div className={styles.field}>
          <label htmlFor="birth-time" className={styles.label}>Время рождения</label>
          {timeKnown ? (
            <input
              id="birth-time"
              type="time"
              value={time}
              onChange={(e) => {
                setTime(e.target.value);
                haptic.selection();
              }}
              className={styles.input}
            />
          ) : (
            <div className={styles.unknownTime}>
              <span>Будет использовано 12:00</span>
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              setTimeKnown(!timeKnown);
              haptic.impact('light');
            }}
            className={styles.toggleButton}
          >
            {timeKnown ? 'Не знаю точное время' : 'Указать время'}
          </button>
          {!timeKnown && (
            <p className={styles.warning}>
              Асцендент и дома будут приблизительными
            </p>
          )}
        </div>

        {/* Location */}
        <div className={styles.field}>
          <label htmlFor="birth-location" className={styles.label}>Место рождения</label>
          {selectedLocation ? (
            <div className={styles.selectedLocation}>
              <span>{selectedLocation.displayNameShort}</span>
              <button
                type="button"
                onClick={() => {
                  clearSelection();
                  haptic.impact('light');
                }}
                className={styles.clearButton}
              >
                ✕
              </button>
            </div>
          ) : (
            <>
              <input
                id="birth-location"
                type="text"
                value={locationQuery}
                onChange={(e) => setLocationQuery(e.target.value)}
                placeholder="Начните вводить город..."
                className={styles.input}
                autoComplete="off"
              />
              {isSearchingLocation && (
                <span className={styles.hint}>Поиск...</span>
              )}
              {locationError && (
                <span className={styles.warning}>{locationError}</span>
              )}
              {!isSearchingLocation && !locationError && locationSearched && locationResults.length === 0 && (
                <span className={styles.hint}>
                  Город не найден. Попробуйте другое написание
                </span>
              )}
              {locationResults.length > 0 && (
                <ul className={styles.locationList}>
                  {locationResults.map((location) => (
                    <li key={`${location.latitude}_${location.longitude}`}>
                      <button
                        type="button"
                        onClick={() => handleLocationSelect(location)}
                        className={styles.locationItem}
                      >
                        {location.displayNameShort}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default BirthDataForm;
